import { useState, useEffect, useCallback } from 'react';
import { Folder, File as FileIcon, ChevronRight, Loader2, RefreshCw, AlertTriangle, Check, FolderTree, Plus, Pencil, Trash2, Save } from 'lucide-react';
import { apiService, PackFileNode, PackContainer, ContainerConflictDTO } from '@/services/api.service';
import { useGameStore } from '@/stores/game.store';
import { useSnapshotDraft, getDraftPayload, draftKey } from '@/stores/unsavedDrafts.store';
import { ConflictList, SyncConflictItem, SyncMessages } from '@/components/config/SyncConflicts';
import chipStyles from '@/components/config/GroupPicker.module.scss';
import form from '@/components/config/PackForm.module.scss';
import styles from './FilesPage.module.scss';

const TEXT_EXT = ['json', 'lang', 'molang', 'txt', 'properties', 'yaml', 'yml', 'conf', 'config', 'cfg', 'toml', 'ini', 'md', 'csv', 'js', 'ts', 'mcfunction'];

function extOf(name: string): string {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i + 1).toLowerCase() : '';
}

export function ServersPage() {
  const activeGameId = useGameStore((s) => s.activeGameId);
  const [containers, setContainers] = useState<PackContainer[]>([]);
  const [id, setId] = useState('');
  const [path, setPath] = useState('');
  const [entries, setEntries] = useState<PackFileNode[]>([]);
  const [selected, setSelected] = useState<PackFileNode | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [status, setStatus] = useState<{ ok: boolean; msg: string } | null>(null);
  // Détail de la dernière synchronisation (travail local gardé par-dessus main) et conflits en attente.
  const [syncDetails, setSyncDetails] = useState<string[]>([]);
  const [conflicts, setConflicts] = useState<ContainerConflictDTO[]>([]);
  const [resolving, setResolving] = useState<string | null>(null);
  const [editing, setEditing] = useState<PackContainer | null>(null);
  const [reloadContainers, setReloadContainers] = useState(0);

  useEffect(() => {
    apiService.getContainers().then((r) => {
      const list = (r.data?.containers ?? []).filter((c) => (c.project ?? 'paladium') === activeGameId);
      setContainers(list);
      setId((prev) => (list.some((c) => c.id === prev) ? prev : list[0]?.id ?? ''));
    });
  }, [activeGameId, reloadContainers]);

  const container = containers.find((c) => c.id === id);
  const rootLabel = container?.name || id;

  const loadDir = useCallback((cid: string, p: string) => {
    if (!cid) { setEntries([]); return; }
    setLoading(true);
    apiService.getContainerTree(cid, p).then((r) => { setEntries(r.data?.entries ?? []); setLoading(false); });
  }, []);

  const loadConflicts = useCallback((cid: string) => {
    if (!cid) { setConflicts([]); return; }
    apiService.getContainerConflicts(cid).then((r) => setConflicts(r.data?.conflicts ?? []));
  }, []);

  useEffect(() => { setPath(''); setSelected(null); setStatus(null); setSyncDetails([]); loadConflicts(id); }, [id, loadConflicts]);
  useEffect(() => { setSelected(null); loadDir(id, path); }, [id, path, loadDir]);

  // La synchronisation garde le travail non publié par-dessus main (sauvegarde, puis fusion JSON clé par
  // clé) : le détail dit combien de fichiers locaux sont conservés ou fusionnés, et liste les conflits.
  const sync = async () => {
    setSyncing(true);
    setStatus(null);
    setSyncDetails([]);
    const r = await apiService.syncContainer(id);
    setSyncing(false);
    setStatus(r.data ? { ok: r.data.success, msg: r.data.message } : { ok: false, msg: r.error ?? 'Échec de la synchronisation' });
    setSyncDetails(r.data?.details ?? []);
    loadConflicts(id);
    loadDir(id, path);
  };

  // Conflit : garder la version du panel (publiée telle quelle) ou prendre celle de main (JSON fusionné :
  // seules les clés en conflit reprennent la valeur de main ; la version du panel reste dans la sauvegarde).
  const settleConflict = async (c: SyncConflictItem, keep: 'panel' | 'github') => {
    setResolving(c.path);
    const r = keep === 'panel'
      ? await apiService.resolveContainerConflicts(id, [c.path])
      : await apiService.takeMainContainerConflicts(id, [c.path]);
    setResolving(null);
    setStatus(r.data ? { ok: r.data.success, msg: `${c.path} : ${r.data.message}` } : { ok: false, msg: r.error ?? 'Échec' });
    loadConflicts(id);
    if (selected?.path === c.path) setSelected(null);
    loadDir(id, path);
  };

  const removeContainer = async () => {
    if (!container) return;
    if (!window.confirm(`Retirer le container « ${container.name} » de la liste ? Les fichiers déjà clonés restent sur le serveur.`)) return;
    const r = await apiService.deleteContainer(container.id);
    setStatus(r.data ? { ok: r.data.success, msg: r.data.message } : { ok: false, msg: r.error ?? 'Échec' });
    if (r.data?.success) { setEditing(null); setReloadContainers((k) => k + 1); }
  };

  const segments = path ? path.split('/') : [];
  const busy = syncing;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        {/* Titre et sous-titre vivent dans ConfigSettingsPage ; l'espaceur garde les actions à droite. */}
        <div />
        <div className={styles.headerActions}>
          <button
            className={styles.downloadBtn}
            onClick={() => setEditing({ id: '', name: '', type: '', repo: '', project: activeGameId, modsPath: 'data/mods' })}
          >
            <Plus size={14} /> Déclarer un container
          </button>
          {container && (
            <button className={styles.downloadBtn} onClick={() => setEditing({ ...container })}>
              <Pencil size={14} /> Modifier
            </button>
          )}
          {container && (
            <button className={styles.downloadBtn} onClick={removeContainer}>
              <Trash2 size={14} /> Retirer
            </button>
          )}
          <button className={styles.saveBtn} onClick={sync} disabled={!id || busy}
            title="La copie de chaque image se remet à jour toute seule (toutes les 3 minutes) quand main bouge ; ce bouton le fait tout de suite.">
            {syncing ? <Loader2 size={14} className={styles.spin} /> : <RefreshCw size={14} />} {syncing ? 'Sync…' : 'Synchroniser'}
          </button>
        </div>
      </header>

      {editing && (
        <ContainerForm
          initial={editing}
          onCancel={() => setEditing(null)}
          onSaved={(msg) => { setEditing(null); setStatus({ ok: true, msg }); setReloadContainers((k) => k + 1); }}
        />
      )}

      <div className={chipStyles.chips}>
        {containers.length === 0 ? (
          <span className={chipStyles.empty}>
            Aucun container pour ce projet — déclare-le pour rendre éditables l'apparence des grades, le chat, la boutique et l'index des cosmetics.
          </span>
        ) : (
          containers.map((c) => {
            const on = c.id === id;
            return (
              <button
                key={c.id}
                className={`${chipStyles.chip} ${on ? chipStyles.chipActive : ''}`}
                style={on ? { borderColor: '#F97316', backgroundColor: '#F9731622', color: '#F97316' } : undefined}
                onClick={() => setId(c.id)}
              >
                <span className={chipStyles.dot} style={{ backgroundColor: '#F97316' }} />
                {c.name}
              </button>
            );
          })
        )}
      </div>

      {status && (
        <div className={status.ok ? styles.ok : styles.error}>
          {status.ok ? <Check size={14} /> : <AlertTriangle size={14} />} {status.msg}
        </div>
      )}

      <SyncMessages messages={syncDetails} />

      <ConflictList
        conflicts={conflicts}
        remoteLabel="main"
        busyPath={resolving}
        disabled={!!resolving || syncing}
        onKeepPanel={(c) => void settleConflict(c, 'panel')}
        onTakeRemote={(c) => void settleConflict(c, 'github')}
        takeTitle={(c) => ((c.keys ?? []).length > 0
          ? 'Seules les clés en conflit reprennent la valeur de main, le reste du travail du panel est gardé.'
          : 'Le fichier revient à la version de main.')}
      />

      {id && (
        <div className={styles.breadcrumb}>
          <button className={styles.crumb} onClick={() => setPath('')}><FolderTree size={14} /> {rootLabel}</button>
          {segments.map((seg, i) => (
            <span key={i} className={styles.crumbGroup}>
              <ChevronRight size={13} className={styles.crumbSep} />
              <button className={styles.crumb} onClick={() => setPath(segments.slice(0, i + 1).join('/'))}>{seg}</button>
            </span>
          ))}
        </div>
      )}

      <div className={styles.split}>
        <div className={styles.listCol}>
          {loading ? (
            <div className={styles.listEmpty}><Loader2 size={18} className={styles.spin} /></div>
          ) : entries.length === 0 ? (
            <div className={styles.listEmpty}>Dossier vide — clique « Synchroniser » pour récupérer les fichiers du container.</div>
          ) : (
            entries.map((e) => (
              <button
                key={e.path}
                className={`${styles.row} ${!e.dir && selected?.path === e.path ? styles.rowActive : ''}`}
                onClick={() => (e.dir ? setPath(e.path) : setSelected(e))}
              >
                {e.dir ? <Folder size={16} className={styles.folderIcon} /> : <FileIcon size={16} className={styles.fileIcon} />}
                <span className={styles.rowName}>{e.name}</span>
                {!e.dir && <span className={styles.rowSize}>{formatSize(e.size)}</span>}
              </button>
            ))
          )}
        </div>

        <div className={styles.detail} key={selected?.path}>
          {selected ? <ConfigEditor containerId={id} node={selected} /> : (
            <div className={styles.detailEmpty}>
              <FolderTree size={40} strokeWidth={1.5} />
              <p>Sélectionne un fichier de config pour l'éditer.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function formatSize(n: number): string {
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
  return `${(n / (1024 * 1024)).toFixed(1)} Mo`;
}

/** Déclaration d'un container de serveur (repo git de config). Sans container déclaré, un projet n'a
 * ni apparence de grade, ni chat, ni boutique, ni index de cosmetics : tout ça vit dans sa config. */
function ContainerForm({
  initial, onCancel, onSaved,
}: {
  initial: PackContainer;
  onCancel: () => void;
  onSaved: (message: string) => void;
}) {
  const [c, setC] = useState<PackContainer>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isNew = !initial.id;
  const set = <K extends keyof PackContainer>(k: K, v: PackContainer[K]) => setC((s) => ({ ...s, [k]: v }));

  const save = async () => {
    if (!c.id.trim()) { setError('Identifiant requis (ex. ng-faction)'); return; }
    if (!c.repo.trim()) { setError('URL du repo requise'); return; }
    setSaving(true);
    setError(null);
    const res = await apiService.saveContainer({
      ...c,
      id: c.id.trim(),
      name: c.name.trim() || c.id.trim(),
      type: c.type.trim() || c.id.trim(),
    });
    setSaving(false);
    if (res.data?.success) onSaved(res.data.message);
    else setError(res.data?.message ?? res.error ?? 'Échec de l\'enregistrement');
  };

  return (
    <div className={form.form}>
      <div className={form.row2}>
        <div className={form.field}>
          <label className={form.label}>Identifiant</label>
          <input className={form.input} value={c.id} disabled={!isNew}
            onChange={(e) => set('id', e.target.value)} placeholder="ex. ng-faction" />
        </div>
        <div className={form.field}>
          <label className={form.label}>Nom affiché</label>
          <input className={form.input} value={c.name} onChange={(e) => set('name', e.target.value)} placeholder="ex. Faction" />
        </div>
      </div>

      <div className={form.field}>
        <label className={form.label}>Repo du container (URL git)</label>
        <input className={form.input} value={c.repo} onChange={(e) => set('repo', e.target.value)}
          placeholder="https://github.com/Paladium-Bedrock/…-Container" />
      </div>

      <div className={form.row2}>
        <div className={form.field}>
          <label className={form.label}>Type de serveur</label>
          <input className={form.input} value={c.type} onChange={(e) => set('type', e.target.value)} placeholder="ex. faction" />
        </div>
        <div className={form.field}>
          <label className={form.label}>Dossier des configs</label>
          <input className={form.input} value={c.modsPath ?? ''} onChange={(e) => set('modsPath', e.target.value)} placeholder="data/mods" />
        </div>
      </div>

      <p className={form.hint}>
        Projet : <strong>{c.project}</strong> (le projet actif). Après l'enregistrement, clique « Synchroniser » pour
        cloner le repo : le panel y cherchera le fichier de config du serveur (celui qui porte PermissionsSettings /
        NametagSettings) pour l'apparence des grades, le chat, la boutique et l'index des cosmetics.
      </p>

      {error && <div className={form.error}><AlertTriangle size={14} /> {error}</div>}

      <div className={form.actions}>
        <button className={form.saveBtn} onClick={save} disabled={saving}>
          <Save size={14} /> {saving ? 'Enregistrement…' : 'Enregistrer'}
        </button>
        <button className={styles.downloadBtn} onClick={onCancel}>Annuler</button>
      </div>
    </div>
  );
}

/** Éditeur texte d'un fichier de config container (JSON validé côté serveur).
 * Enregistrement via la barre du bas (comme blocks/items) ; survit au changement de fichier. */
function ConfigEditor({ containerId, node }: { containerId: string; node: PackFileNode }) {
  const [text, setText] = useState('');
  const [orig, setOrig] = useState('');
  const [loading, setLoading] = useState(true);
  const editable = TEXT_EXT.includes(extOf(node.name));

  useEffect(() => {
    let active = true;
    setLoading(true);
    apiService.getContainerFileRaw(containerId, node.path).then((t) => {
      if (!active) return;
      const disk = t ?? '';
      setOrig(disk);
      const pending = getDraftPayload(draftKey('container-file', containerId, node.path)) as string | undefined;
      setText(pending ?? disk);
      setLoading(false);
    });
    return () => { active = false; };
  }, [containerId, node.path]);

  const dirty = editable && !loading && text !== orig;

  const save = async () => {
    const res = await apiService.saveContainerFile(containerId, node.path, text);
    const ok = !!res.data?.success;
    if (ok) setOrig(text);
    return ok;
  };

  useSnapshotDraft({
    kind: 'container-file',
    groupId: containerId,
    identifier: node.path,
    label: `${containerId} · ${node.name}`,
    dirty,
    payload: text,
    save,
  });

  if (loading) return <div className={styles.viewer}><Loader2 size={18} className={styles.spin} /> Chargement…</div>;
  if (!editable) return <div className={styles.viewer}><div className={styles.viewerHead}>{node.path}</div><p className={styles.viewerNote}>Type de fichier non éditable ici.</p></div>;

  return (
    <div className={styles.viewer}>
      <div className={styles.viewerHead}>{node.path}</div>
      <textarea className={styles.textarea} value={text} onChange={(e) => setText(e.target.value)} spellCheck={false} />
      <p className={styles.viewerNote}>
        {dirty ? 'Modifications en attente — enregistre-les via la barre du bas, puis « Déployer ».' : 'Enregistré. Clique « Déployer » pour publier une version.'}
      </p>
    </div>
  );
}
